import { useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Compass } from "lucide-react";
import logoPrincipal from "@/assets/logo-principal.png";

const ease = [0.22, 1, 0.36, 1] as const;

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    console.error("404 Error: User attempted to access non-existent route:", location.pathname);
  }, [location.pathname]);

  // A hospedagem não permite um arquivo de 404 próprio: qualquer endereço
  // inexistente recebe o HTML da home. Aqui, com o JavaScript rodando, marcamos
  // a página como não indexável e tiramos a canônica da home — senão o Google
  // leria "/rota-que-não-existe" como cópia de "/".
  useEffect(() => {
    const robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const valorAnterior = robots?.content ?? null;
    if (robots) robots.content = "noindex, follow";
    else {
      const el = document.createElement("meta");
      el.name = "robots";
      el.content = "noindex, follow";
      el.dataset.m360NotFound = "1";
      document.head.appendChild(el);
    }

    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    const hrefAnterior = canonical?.getAttribute("href") ?? null;
    canonical?.remove();

    // O título só muda aqui, onde já sabemos que é endereço inexistente de fato.
    const tituloAnterior = document.title;
    document.title = "Página não encontrada — Materna360";

    return () => {
      document.title = tituloAnterior;
      const atual = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
      if (atual?.dataset.m360NotFound === "1") atual.remove();
      else if (atual && valorAnterior !== null) atual.content = valorAnterior;
      if (canonical && hrefAnterior) {
        canonical.setAttribute("href", hrefAnterior);
        document.head.appendChild(canonical);
      }
    };
  }, []);

  return (
    <div className="min-h-[100dvh] safe-top flex flex-col relative overflow-hidden bg-background">
      {/* Ambient glow */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          background: `
            radial-gradient(ellipse 70% 55% at 70% 15%, hsla(330, 100%, 93%, 0.4) 0%, transparent 55%),
            radial-gradient(ellipse 55% 45% at 25% 80%, hsla(15, 70%, 95%, 0.3) 0%, transparent 50%),
            hsl(var(--background))
          `,
        }}
      />

      {/* Logo */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease }}
        className="relative z-10 px-6 pt-8 md:pt-10"
      >
        <img
          src={logoPrincipal}
          alt="Materna360"
          className="h-5 md:h-6 w-auto opacity-60"
        />
      </motion.div>

      {/* Content */}
      <main className="relative z-10 flex-1 flex flex-col items-center justify-center px-6 md:px-12 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15, ease }}
          className="w-16 h-16 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: "hsl(var(--soft) / 0.6)" }}
        >
          <Compass className="w-7 h-7 text-brand/50" strokeWidth={1.5} />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease }}
          className="eyebrow mb-4"
        >
          Página não encontrada
        </motion.p>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3, ease }}
          className="text-[32px] md:text-[44px] font-normal leading-[1.05] tracking-[-0.02em] font-display mb-3"
          style={{ color: "hsl(var(--text-strong))" }}
        >
          Ops, esse caminho
          <br />
          não existe<span className="text-brand">.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.45, ease }}
          className="text-[14px] leading-[1.65] max-w-[320px] mb-8"
          style={{ color: "hsl(var(--text-strong) / 0.45)" }}
        >
          Sem problema — acontece. Que tal voltar para onde você estava?
        </motion.p>

        <motion.button
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6, ease }}
          whileHover={{ scale: 1.02, boxShadow: "0 8px 32px rgba(253, 37, 151, 0.25)" }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate("/perfil")}
          className="cta-primary px-7 py-3.5 text-[13px] flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          Voltar ao início
        </motion.button>
      </main>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.7 }}
        className="relative z-10 pb-8 md:pb-10"
      >
        <p className="footer-text">
          Materna360 © {new Date().getFullYear()}
        </p>
      </motion.footer>
    </div>
  );
};

export default NotFound;
